/* Sélecteur « Depuis un modèle » — bouton + pop-over listant les modèles de l'org.
   À la sélection, appelle onApply(template) fourni par l'écran (facture ou devis),
   qui applique le pré-remplissage (lignes figées, délai/notes selon l'écran).
   Réutilise le patron d'interaction du picker catalogue (clic hors zone + Échap). */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../lib/icons';
import { eur } from './ui';
import { useTemplates, type InvoiceTemplate } from '../data/live';

export function TemplatePicker({ onApply }: { onApply: (t: InvoiceTemplate) => void }) {
  const { rows } = useTemplates();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (rows.length === 0) return null; // pas de modèle → pas de bouton

  const filtered = rows.filter((t) => t.name.toLowerCase().includes(q.trim().toLowerCase()));
  function totalHt(t: InvoiceTemplate): number {
    return t.lines.reduce((s, l) => s + (l.unitPriceCents / 100) * l.quantity, 0);
  }

  return (
    <div ref={popRef} style={{ display: 'inline-flex', position: 'relative', marginBottom: 12 }}>
      <button className="btn btn--soft btn--sm" onClick={() => setOpen((o) => !o)}>
        <Icon name="copy" size={15} />
        Depuis un modèle
      </button>
      {open && (
        <div className="catalog-pop" role="listbox" aria-label="Modèles" style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 20,
          width: 340, maxHeight: 280, overflowY: 'auto',
          background: 'var(--surface, #fff)', border: '1px solid var(--border, #ddd)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 8,
        }}>
          <input
            className="ctrl" autoFocus placeholder="Rechercher un modèle…" aria-label="Rechercher un modèle"
            value={q} onChange={(ev) => setQ(ev.target.value)} style={{ width: '100%', marginBottom: 6 }}
          />
          {filtered.length === 0 && <div style={{ padding: 8, opacity: 0.6 }}>Aucun modèle.</div>}
          {filtered.map((t) => (
            <button
              key={t.id} className="catalog-pop__item" role="option"
              onClick={() => { onApply(t); setOpen(false); setQ(''); }}
              style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 12, padding: '8px 10px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <span>{t.name}</span>
              <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>{eur(totalHt(t))} HT</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
